import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Coffee, 
  Leaf, 
  Users, 
  Truck, 
  Recycle, 
  ArrowRight, 
  Check, 
  QrCode,
  Globe, 
  DollarSign,
  Droplets,
  ShoppingBag,
  Calendar,
  Star,
  BarChart,
  Sprout,
  ShieldCheck,
  Zap,
  CircleDollarSign,
  Sparkles,
  MapPin
} from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import { Badge, Button } from '../components/ui';

const UnityBeanPage: React.FC = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  // Product data for shop section
  const products = [
    {
      id: "unity-bean-1",
      name: "Unity Bean Classic",
      roast: "Medium",
      origin: "Jinja, Uganda",
      notes: "Chocolate, Caramel, Subtle Citrus",
      price: "$18.99",
      image: "https://images.unsplash.com/photo-1559525839-b184a4d698c7"
    },
    {
      id: "unity-bean-2",
      name: "Unity Bean Dark",
      roast: "Dark",
      origin: "Jinja, Uganda",
      notes: "Smoky, Rich, Nutty, Low Acidity",
      price: "$18.99",
      image: "https://images.unsplash.com/photo-1587050669551-4097934d2da9"
    },
    {
      id: "unity-bean-3",
      name: "Unity Bean Light",
      roast: "Light",
      origin: "Jinja, Uganda",
      notes: "Bright, Fruity, Floral Notes",
      price: "$19.99",
      image: "https://images.unsplash.com/photo-1611854779393-1b2da9d400fe"
    }
  ];
  
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative min-h-[90vh]">
        <div className="absolute inset-0 z-0">
          {/* Background image as fallback while video loads */}
          <img 
            src="https://images.unsplash.com/photo-1497935586351-b67a49e012bf" 
            alt="Coffee farmers in Uganda"
            className={`w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
          />
          
          {/* Video background */}
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoadedData={() => setVideoLoaded(true)}
          >
            <source src="https://player.vimeo.com/external/370331493.hd.mp4?s=ce49c8c6d8e28a89298ffb4c53a2e842bdb01330&profile_id=170&oauth2_token_id=57447761" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
        
        <div className="relative container mx-auto px-4 h-full flex items-center justify-center z-10">
          <AnimatedSection>
            <div className="max-w-3xl text-center">
              <Badge 
                variant="purple" 
                className="px-4 py-2 text-sm mb-8 bg-avn-purple-600 text-white"
                icon={<Coffee className="h-4 w-4" />}
              >
                Unity Brand
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Coffee That Restores
              </h1>
              <p className="text-2xl text-white/90 mb-10 leading-relaxed">
                From seed to sip, Unity Bean reconnects us to the land, the farmer, and the future.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-white text-avn-purple-700 border-white hover:bg-white/90"
                >
                  <a href="#shop" className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    <span>Shop Now</span>
                  </a>
                </Button>
                
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-transparent text-white border-white hover:bg-white/10"
                >
                  <a href="#trace" className="flex items-center gap-2">
                    <QrCode className="h-5 w-5" />
                    <span>Trace Your Cup</span>
                  </a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
      
      {/* More Than a Cup of Coffee Section */}
      <div className="py-20 bg-white" id="about">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                More Than a Cup of Coffee
              </h2>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed">
                Every cup of coffee touches lives, ecosystems, and entire economies. But most farmers earn less than 10% of the retail price. Unity Bean flips that model — giving up to 50% of value directly to producers, using regenerative farming, solar-powered mobile roasters, and traceability technology developed by AVN.
              </p>
            
              <div className="grid md:grid-cols-2 gap-8 mt-12 items-center">
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold mb-4">Typical Coffee Chain</h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Farmer</span>
                      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">6-10%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: '8%' }}></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Middlemen & Exporters</span>
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm">30-40%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: '35%' }}></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Roasters & Retailers</span>
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">50-60%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '57%' }}></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold mb-4">Unity Bean Model</h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Farmer</span>
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">25-50%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: '50%' }}></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Processing & Logistics</span>
                      <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">20%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: '20%' }}></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">AVN Platform</span>
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">30%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-200 rounded-full">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '30%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
      
      {/* Traceability & AVN Integration Section */}
      <div className="py-20 bg-gray-50" id="trace">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <AnimatedSection>
              <div>
                <Badge 
                  variant="purple" 
                  className="mb-6"
                  icon={<QrCode className="h-4 w-4" />}
                >
                  AVN Technology
                </Badge>
                <h2 className="text-4xl font-bold text-gray-900 mb-8">
                  Trace Every Bean
                </h2>
                <p className="text-xl text-gray-700 mb-6">
                  Each Unity Bean bag includes a QR code backed by AVN-Earth AI. Scan it to meet your farmer, view roast data, and see the water and carbon footprint of your cup.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                      <Users className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Meet Your Farmer</h3>
                      <p className="text-gray-600">See the faces behind your coffee and their stories</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                      <Leaf className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Environmental Impact</h3>
                      <p className="text-gray-600">Track water usage, carbon footprint, and regenerative practices</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                      <DollarSign className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Payment Transparency</h3>
                      <p className="text-gray-600">See exactly how much farmers were paid for your beans</p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="bg-avn-purple-600 hover:bg-avn-purple-700"
                >
                  <a href="#demo" className="flex items-center gap-2">
                    <QrCode className="h-5 w-5" />
                    <span>Try Demo Scanner</span>
                  </a>
                </Button>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1622465413573-4b906b790814"
                  alt="QR Code Scanning" 
                  className="rounded-xl shadow-xl w-full"
                />
                <div className="absolute -bottom-5 -right-5 bg-white p-5 rounded-xl shadow-xl max-w-xs">
                  <div className="flex gap-3 items-start">
                    <QrCode className="h-8 w-8 text-avn-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Trace ID: #UB24071</h4>
                      <p className="text-sm text-gray-600">Coffee sourced from Edward's Hamlet, roasted on July 3, 2024</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Shop Section */}
      <div className="py-20 bg-white" id="shop">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <Badge 
                variant="purple" 
                className="mb-6"
                icon={<ShoppingBag className="h-4 w-4" />}
              >
                Shop Unity Bean
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Ethical Coffee Selection
              </h2>
              <p className="text-xl text-gray-700">
                Directly sourced from Edward's Hamlet in Jinja, Uganda. Each purchase supports our farmers and regenerative practices.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {products.map((product) => (
              <AnimatedSection key={product.id} delay={0.1}>
                <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className="h-64 relative">
                    <img 
                      src={product.image}
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 right-4">
                      <Badge variant="amber" className="bg-amber-100/80 backdrop-blur-sm">
                        {product.roast} Roast
                      </Badge>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-sm bg-gray-100 px-3 py-1 rounded-full">
                        {product.origin}
                      </span>
                      <div className="text-sm bg-gray-100 px-3 py-1 rounded-full flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        <span>5.0 (24)</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">
                      <span className="font-medium">Tasting Notes: </span>
                      {product.notes}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-avn-purple-700">{product.price}</span>
                      <Button 
                        variant="primary" 
                        className="bg-avn-purple-600 hover:bg-avn-purple-700"
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              variant="secondary" 
              size="lg" 
              className="bg-white border-avn-purple-200 text-avn-purple-700 hover:bg-avn-purple-50"
            >
              <a href="#" className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" />
                <span>View All Products</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Impact Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <Badge 
                variant="green" 
                className="mb-6"
                icon={<Leaf className="h-4 w-4" />}
              >
                Real Impact
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Your Coffee, Their Future
              </h2>
              <p className="text-xl text-gray-700">
                Every Unity Bean purchase drives real-world positive change for farmers, communities, and the environment.
              </p>
            </div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <AnimatedSection>
              <div className="bg-white p-8 rounded-xl shadow-xl text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-3">25-50%</h3>
                <p className="text-gray-700">
                  of profits go directly to farmers, compared to just 6% in traditional supply chains
                </p>
                
                <div className="mt-8 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '50%' }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="h-full bg-gradient-to-r from-green-400 to-green-600"
                  ></motion.div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="bg-white p-8 rounded-xl shadow-xl text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Droplets className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold mb-3">5 Litres</h3>
                <p className="text-gray-700">
                  of clean water restored for every bag of Unity Bean coffee purchased
                </p>
                
                <div className="mt-8 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '70%' }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600"
                  ></motion.div>
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <div className="bg-white p-8 rounded-xl shadow-xl text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShieldCheck className="h-10 w-10 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold mb-3">100%</h3>
                <p className="text-gray-700">
                  traceable and verified exploitation-free through AVN-Earth AI technology
                </p>
                
                <div className="mt-8 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                    className="h-full bg-gradient-to-r from-purple-400 to-purple-600"
                  ></motion.div>
                </div>
              </div>
            </AnimatedSection>
          </div>
          
          <div className="mt-16 max-w-3xl mx-auto">
            <AnimatedSection delay={0.4}>
              <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-avn-purple-100 rounded-full mt-1">
                    <Truck className="h-6 w-6 text-avn-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3">Unity Bean Rover</h3>
                    <p className="text-gray-600 mb-4">
                      Our solar-powered mobile processing unit revolutionizes rural sourcing, bringing technology directly to remote farming communities.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Eliminates exploitative middlemen</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Reduces transport emissions</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Preserves coffee freshness</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span>Solar-powered operation</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Subscription Callout Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <div className="bg-avn-purple-50 p-8 md:p-12 rounded-xl shadow-lg border border-avn-purple-100">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="md:w-2/3">
                    <Badge 
                      variant="purple" 
                      className="mb-4"
                      icon={<Calendar className="h-4 w-4" />}
                    >
                      Monthly Delivery
                    </Badge>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      Subscribe to Justice
                    </h2>
                    <p className="text-lg text-gray-700 mb-6">
                      Get ethically sourced coffee delivered monthly, and deepen your impact with every sip. Save 10% on every order with a subscription.
                    </p>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Free shipping on all subscription orders</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Flexible delivery - pause or cancel anytime</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Special subscriber-only offerings</span>
                      </div>
                    </div>
                    <Button 
                      variant="primary" 
                      size="lg" 
                      className="bg-avn-purple-600 hover:bg-avn-purple-700"
                    >
                      <a href="#shop" className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        <span>Explore Subscription Plans</span>
                      </a>
                    </Button>
                  </div>
                  <div className="md:w-1/3">
                    <img 
                      src="https://images.unsplash.com/photo-1516647845672-f19d101f54ff" 
                      alt="Coffee Subscription" 
                      className="rounded-xl shadow-md"
                    />
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Unity Brand - Beyond Coffee Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <AnimatedSection>
              <Badge 
                variant="purple" 
                className="mb-4"
                icon={<Sparkles className="h-4 w-4" />}
              >
                Unity Brand
              </Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Beyond Coffee – The Movement Expands
              </h2>
              <p className="text-xl text-gray-700">
                Unity Bean is just the beginning. AVN is building ethical supply chains across multiple agricultural products.
              </p>
            </AnimatedSection>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <AnimatedSection delay={0.1}>
              <div className="bg-white p-8 rounded-xl shadow-md text-center h-full">
                <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sprout className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">Unity Cocoa</h3>
                <p className="text-gray-600">
                  Ethically sourced cocoa with full traceability from farm to chocolate bar.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="bg-white p-8 rounded-xl shadow-md text-center h-full">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Leaf className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">Unity Moringa</h3>
                <p className="text-gray-600">
                  Nutrient-dense superfood grown with regenerative practices and fair compensation.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.3}>
              <div className="bg-white p-8 rounded-xl shadow-md text-center h-full">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Coffee className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">Unity Tea</h3>
                <p className="text-gray-600">
                  Transparent tea supply chain with QR-code traceability to specific gardens.
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.4}>
              <div className="bg-white p-8 rounded-xl shadow-md text-center h-full">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-3">Unity Vanilla</h3>
                <p className="text-gray-600">
                  Sustainably grown vanilla pods with direct farmer relationships and transparent pricing.
                </p>
              </div>
            </AnimatedSection>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Each Unity Brand product is built on the same principles: traceability, equity, and localized regenerative systems.
            </p>
          </div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div id="notOnOurPlates" className="py-24 bg-avn-purple-900 text-white">
        <div className="container mx-auto px-4">
          <div className="relative">
            <div className="absolute inset-0 opacity-20">
              <img
                src="https://images.unsplash.com/photo-1506806732259-39c2d0268443"
                alt="Coffee beans" 
                className="w-full h-full object-cover"
              />
            </div>
            <AnimatedSection>
              <div className="relative z-10 max-w-3xl mx-auto text-center">
                <h2 className="text-5xl font-bold mb-8 leading-tight">
                  "Not just a cup of coffee — a movement in your hands."
                </h2>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="bg-white text-avn-purple-900 border-white hover:bg-white/90"
                  >
                    <a href="#shop" className="flex items-center gap-2">
                      <ShoppingBag className="h-5 w-5" />
                      <span>Buy Unity Bean</span>
                    </a>
                  </Button>
                  <Button 
                    variant="outline"  
                    size="lg"
                    className="bg-transparent text-white border-white hover:bg-white/10"
                  >
                    <Link to="/avn" className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      <span>Learn About AVN</span>
                    </Link>
                  </Button>
                </div>

                <div className="mt-16 pt-12 border-t border-white/20 grid md:grid-cols-3 gap-8">
                  <div>
                    <h3 className="font-bold text-xl mb-2">Follow Us</h3>
                    <div className="flex gap-4 justify-center">
                      <a href="#" className="text-white hover:text-white/80">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                      </a>
                      <a href="#" className="text-white hover:text-white/80">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                      </a>
                      <a href="#" className="text-white hover:text-white/80">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                      </a>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">Shop</h3>
                    <div className="flex flex-col gap-2 text-sm">
                      <a href="#" className="text-white/80 hover:text-white">All Products</a>
                      <a href="#" className="text-white/80 hover:text-white">Subscriptions</a>
                      <a href="#" className="text-white/80 hover:text-white">Gift Cards</a>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">Contact</h3>
                    <div className="text-sm text-white/80">
                      <p>info@avnearth.com</p>
                      <p>+44 794 950 0433</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnityBeanPage;